import {
  type Column,
  flexRender,
  type Row,
  type RowData,
} from "@tanstack/react-table";
import { type CSSProperties, memo, type ReactElement } from "react";

import { cn } from "../../utils";
import { TableCell, TableRow } from "../table";

export const getPinningStyles = <TData extends RowData>(
  column: Column<TData>,
): CSSProperties => {
  const isPinned = column.getIsPinned();
  return {
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

interface IMemoizedTableRowProps<TData extends RowData> {
  row: Row<TData>;
  enableRowSelection: boolean;
  getRowClassName?: (row: TData) => string | undefined;
}

export const MemoizedTableRow = memo(
  <TData extends RowData>({
    row,
    enableRowSelection,
    getRowClassName,
  }: IMemoizedTableRowProps<TData>) => {
    return (
      <TableRow
        data-state={enableRowSelection && row.getIsSelected() && "selected"}
        className={cn("group bg-white", getRowClassName?.(row.original))}
      >
        {row.getVisibleCells().map((cell) => {
          const { column } = cell;
          const isPinned = column.getIsPinned();
          const dataLastCol: "left" | "right" | undefined =
            isPinned === "left" && column.getIsLastColumn("left")
              ? "left"
              : isPinned === "right" && column.getIsFirstColumn("right")
                ? "right"
                : undefined;

          return (
            <TableCell
              key={cell.id}
              className={cn(
                // transparent so row hover:bg-gray-100/50 shows through; pinned need solid bg for sticky
                // "bg-transparent",
                isPinned && "bg-white",
                [
                  "[&[data-pinned=left][data-last-col=left]]:border-r",
                  "[&[data-pinned=right][data-last-col=right]]:border-l",

                  // shadow looks weird when pinned
                  // "[&[data-pinned=right][data-last-col=right]]:shadow-lg",
                  // "[&[data-pinned=left][data-last-col=left]]:shadow-lg",

                  "[&[data-pinned][data-last-col]]:border-gray-200",
                ],
                column.columnDef.meta?.cellClassName,
                // "backdrop-blur-md",
              )}
              style={getPinningStyles(column)}
              data-pinned={isPinned || undefined}
              data-last-col={dataLastCol}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
      </TableRow>
    );
  },
) as <TData extends RowData>(
  props: IMemoizedTableRowProps<TData>,
) => ReactElement | null;
